import {
  fetchBridgePolicyOnFileDisplay,
  readReusableBridgePolicyFromProjection,
  type BridgePolicyOnFileDisplay,
} from '@/bridge/bridgePolicyReuse'
import type { BridgePolicyId, CustomerId } from '@gh/common/models'

export type ReusableBridgePolicyPrefetch = {
  policyId: BridgePolicyId | null
  onFileDisplay: BridgePolicyOnFileDisplay | null
}

/** Reuse hub/plan-picker prefetch results for up to five minutes per customer. */
export const REUSABLE_BRIDGE_POLICY_PREFETCH_TTL_MS = 5 * 60_000
const COALESCE_WINDOW_MS = REUSABLE_BRIDGE_POLICY_PREFETCH_TTL_MS - 1_000

let inFlight: {
  customerId: CustomerId
  promise: Promise<ReusableBridgePolicyPrefetch>
} | null = null

let cached: {
  customerId: CustomerId
  fetchedAt: number
  result: ReusableBridgePolicyPrefetch
  epoch: number
} | null = null

let globalPrefetchEpoch = 0
const customerPrefetchEpoch = new Map<CustomerId, number>()

function customerPrefetchEpochFor(customerId: CustomerId): number {
  return customerPrefetchEpoch.get(customerId) ?? 0
}

export function invalidateReusableBridgePolicyPrefetch(customerId?: CustomerId): void {
  globalPrefetchEpoch += 1
  if (customerId) {
    customerPrefetchEpoch.set(customerId, (customerPrefetchEpoch.get(customerId) ?? 0) + 1)
    if (cached?.customerId === customerId) {
      cached = null
    }
    if (inFlight?.customerId === customerId) {
      inFlight = null
    }
    return
  }
  customerPrefetchEpoch.clear()
  cached = null
  inFlight = null
}

/** Firestore projection read, then optional Bridge policy display fetch; coalesced per customer. */
export function prefetchReusableBridgePolicyCoalesced(
  customerId: CustomerId,
): Promise<ReusableBridgePolicyPrefetch> {
  const now = Date.now()
  if (inFlight?.customerId === customerId) {
    return inFlight.promise
  }
  if (
    cached?.customerId === customerId &&
    now - cached.fetchedAt < COALESCE_WINDOW_MS &&
    cached.epoch === customerPrefetchEpochFor(customerId)
  ) {
    return Promise.resolve(cached.result)
  }
  const startGlobalEpoch = globalPrefetchEpoch
  const startCustomerEpoch = customerPrefetchEpochFor(customerId)
  const promise = (async (): Promise<ReusableBridgePolicyPrefetch> => {
    const policyId = await readReusableBridgePolicyFromProjection(customerId)
    if (!policyId) {
      return { policyId: null, onFileDisplay: null }
    }
    try {
      const result = {
        policyId,
        onFileDisplay: await fetchBridgePolicyOnFileDisplay(policyId),
      }
      if (
        startGlobalEpoch === globalPrefetchEpoch &&
        startCustomerEpoch === customerPrefetchEpochFor(customerId)
      ) {
        cached = {
          customerId,
          fetchedAt: Date.now(),
          result,
          epoch: startCustomerEpoch,
        }
      }
      return result
    } catch {
      return { policyId, onFileDisplay: null }
    }
  })()
  inFlight = { customerId, promise }
  void promise
    .finally(() => {
      if (inFlight?.promise === promise) {
        inFlight = null
      }
    })
    .catch(() => undefined)
  return promise
}
